// app/javascript/controllers/ats_cover_letter_controller.js
//
// Cover Letter panel. The per-resume endpoints (generate/save/pdf) all need
// the selected resume id, which the coordinator owns and pushes here via
// resumeIdValue. Endpoints match the real API (legacy lines 440-479):
//   POST  /api/v1/ats_optimizer/resumes/:id/generate-cover-letter -> { content }
//   PATCH /api/v1/ats_optimizer/resumes/:id/cover-letter          { content }
//   GET   /api/v1/ats_optimizer/resumes/:id/cover-letter/pdf
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "status"]
  static values = { resumeId: String }

  // Called by the coordinator after Optimize Match (payload.cover_letter).
  apply(payload) {
    if (payload?.cover_letter == null) return
    this.textareaTarget.value = payload.cover_letter
    this.syncPreview()
  }

  // The right-pane preview lives outside this controller's DOM subtree, so we
  // can't write it directly — dispatch to the coordinator, which owns it.
  syncPreview() {
    this.dispatch("cover-letter-changed", {
      detail: { text: this.textareaTarget.value },
      prefix: "ats-optimizer"
    })
  }

  async generate() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    this.#status("Generating cover letter...")
    try {
      const data = await this.#json(
        `/api/v1/ats_optimizer/resumes/${encodeURIComponent(this.resumeIdValue)}/generate-cover-letter`,
        "POST", {}
      )
      this.textareaTarget.value = data.content || ""
      this.syncPreview()
      this.#status("Cover letter generated")
    } catch (e) {
      this.#status(`Generate failed: ${e.message}`)
    }
  }

  async save() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    try {
      await this.#json(
        `/api/v1/ats_optimizer/resumes/${encodeURIComponent(this.resumeIdValue)}/cover-letter`,
        "PATCH", { content: this.textareaTarget.value }
      )
      this.#status("Cover letter saved")
    } catch (e) {
      this.#status(`Save failed: ${e.message}`)
    }
  }

  downloadPdf() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    window.location.href =
      `/api/v1/ats_optimizer/resumes/${encodeURIComponent(this.resumeIdValue)}/cover-letter/pdf`
  }

  async #json(url, method, body) {
    const r = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify(body || {})
    })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return r.json().catch(() => ({}))
  }

  #status(msg) { if (this.hasStatusTarget) this.statusTarget.textContent = msg }
}
